import {
  Card,
  CardContent,
  Grid,
  Typography,
  List,
  ListItem,
  ListItemText,
  Divider,
  Box,
  CircularProgress,
} from "@mui/material";
import { useGetGrantVestmentDistributionQuery } from "hooks/use-services/grant-vestment.service";
import useIntersectionObserver from "hooks/use-services/useIntersectionObserver";

import { AuthContext } from "providers/userProvider";
import { FC, useContext, useMemo } from "react";
import { neutral, primary } from "style/color";
import { AuthData } from "types/auth";
import { FundGvDistribution } from "types/fund-gv-distribution";

import { shortFormat } from "utils/date-format";
import { moneyFormat } from "utils/money-format";

interface GrantVestmentDistributionsListProps {
  grantvestmentId: number;
}

const GrantVestmentDistributionsList: FC<
  GrantVestmentDistributionsListProps
> = ({ grantvestmentId }) => {
  const { selectedAccount } = useContext<AuthData>(AuthContext);

  const {
    data: paginatedData,
    isFetching,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useGetGrantVestmentDistributionQuery({
    limit: 6,
    filter: {
      fundAccountId: selectedAccount?.fundAccountId!,
      grantVestmentId: grantvestmentId,
    },
  });

  const requestsList = useMemo<FundGvDistribution[]>(
    () =>
      paginatedData ? paginatedData?.pages?.flatMap((item) => item.data) : [],
    [paginatedData]
  );
  const lastElementRef = useIntersectionObserver({
    isFetching,
    isLoading,
    fetchNextPage,
    hasNextPage,
  });

  return isLoading ? (
    <Grid item xs={12} sm={10} textAlign={"center"} sx={{ m: 3 }}>
      <CircularProgress color="primary" />
    </Grid>
  ) : (
    <Card sx={{ boxShadow: "none", p: 4 }}>
      <CardContent>
        {requestsList.length !== 0 ? (
          <>
            {requestsList.map(
              (distribution: FundGvDistribution, index: number) => {
                const { amountDistributed, dpi, distributionDate, comment } =
                  distribution;
                return (
                  <List className="successor">
                    <ListItem key={index}>
                      <Grid alignItems="center" container>
                        <Grid item xs={12} sm={3}>
                          <ListItemText
                            sx={{
                              display: "inline-grid",
                              width: "auto",
                              flex: "none",
                            }}
                            primary={
                              <Typography
                                variant="bodyCopyBold"
                                sx={{ color: primary[500], mb: 0.5 }}
                              >
                                Distributed amount
                              </Typography>
                            }
                            secondary={
                              <Typography
                                sx={{ color: neutral[600] }}
                                variant="smallCopyBold"
                              >
                                ${moneyFormat(amountDistributed)}
                              </Typography>
                            }
                          />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <ListItemText
                            sx={{
                              display: "inline-grid",
                              width: "auto",
                              flex: "none",
                            }}
                            primary={
                              <Typography
                                variant="bodyCopyBold"
                                sx={{ color: primary[500], mb: 0.5 }}
                              >
                                Distribution Date
                              </Typography>
                            }
                            secondary={
                              <Typography
                                sx={{ color: neutral[600] }}
                                variant="smallCopyBold"
                              >
                                {shortFormat(distributionDate)}
                              </Typography>
                            }
                          />
                        </Grid>

                        <Grid item xs={12} sm={3}>
                          <ListItemText
                            sx={{
                              display: "inline-grid",
                              width: "auto",
                              flex: "none",
                            }}
                            primary={
                              <Typography
                                variant="bodyCopyBold"
                                sx={{ color: primary[500], mb: 0.5 }}
                              >
                                DPI
                              </Typography>
                            }
                            secondary={
                              <Typography
                                sx={{ color: neutral[600] }}
                                variant="smallCopyBold"
                              >
                                {dpi}
                              </Typography>
                            }
                          />
                        </Grid>

                        <Grid item xs={12} sm={3}>
                          <ListItemText
                            sx={{
                              display: "inline-grid",
                              width: "auto",
                              flex: "none",
                            }}
                            primary={
                              <Typography
                                variant="bodyCopyBold"
                                sx={{ color: primary[500], mb: 0.5 }}
                              >
                                Comment
                              </Typography>
                            }
                            secondary={
                              <Typography
                                sx={{ color: neutral[600] }}
                                variant="smallCopyBold"
                              >
                                {comment}
                              </Typography>
                            }
                          />
                        </Grid>
                      </Grid>
                    </ListItem>
                    {index !== requestsList.length - 1 && (
                      <Divider key={index + 100} />
                    )}
                  </List>
                );
              }
            )}
            {
              <Box ref={lastElementRef}>
                {isFetchingNextPage && (
                  <Grid item xs={12} sm={10} textAlign={"center"} sx={{ m: 3 }}>
                    {" "}
                    <CircularProgress color="primary" />
                  </Grid>
                )}
              </Box>
            }
          </>
        ) : (
          <Typography
            sx={{ mt: 4, color: neutral[600] }}
            variant="bodyCopyRegular"
          >
            You don't have any distribution.
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default GrantVestmentDistributionsList;
